/**
 * Offline device unlock — PBKDF2 verifier in IndexedDB.
 * Never blocks job logging on network. Server session is separate (sync only).
 */
import { getMeta, setMeta } from '../db/jobsDb.js';

/** How long the device stays unlocked without re-entering the password. */
export const LOCAL_UNLOCK_DAYS = 14;

const META_KEY = 'local_unlock_v1';

type StoredUnlock = {
  saltB64: string;
  hashB64: string;
  /** ISO timestamp — skip gate while before this. */
  unlockedUntil: string;
};

const PBKDF2_ITERATIONS = 210_000;
const HASH_BITS = 256;

function b64(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromB64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

async function derive(password: string, salt: Uint8Array): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const saltCopy = new Uint8Array(salt);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltCopy,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    HASH_BITS,
  );
  return new Uint8Array(bits);
}

async function readStored(): Promise<StoredUnlock | null> {
  const raw = await getMeta(META_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredUnlock;
    if (!parsed.saltB64 || !parsed.hashB64 || !parsed.unlockedUntil) return null;
    return parsed;
  } catch {
    return null;
  }
}

async function writeStored(v: StoredUnlock): Promise<void> {
  await setMeta(META_KEY, JSON.stringify(v));
}

export function unlockExpiryFromNow(days = LOCAL_UNLOCK_DAYS): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

/** True if this device still has a valid local unlock window (no password prompt). */
export async function isLocallyUnlocked(): Promise<boolean> {
  const stored = await readStored();
  if (!stored) return false;
  return Date.parse(stored.unlockedUntil) > Date.now();
}

export async function hasLocalVerifier(): Promise<boolean> {
  const stored = await readStored();
  return Boolean(stored?.saltB64 && stored?.hashB64);
}

/**
 * Verify password against the local PBKDF2 hash (fully offline).
 * Extends unlockedUntil on success.
 */
export async function unlockWithLocalPassword(
  password: string,
): Promise<boolean> {
  const stored = await readStored();
  if (!stored) return false;
  const salt = fromB64(stored.saltB64);
  const expected = fromB64(stored.hashB64);
  const got = await derive(password, salt);
  if (!timingSafeEqual(got, expected)) return false;
  await writeStored({
    ...stored,
    unlockedUntil: unlockExpiryFromNow(),
  });
  return true;
}

/** After a successful server login (or first setup), store/replace the local verifier. */
export async function storeLocalVerifier(password: string): Promise<void> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hash = await derive(password, salt);
  await writeStored({
    saltB64: b64(salt),
    hashB64: b64(hash),
    unlockedUntil: unlockExpiryFromNow(),
  });
}

/** End the unlock window (prompt next open) but keep the verifier for offline re-entry. */
export async function clearLocalUnlockSession(): Promise<void> {
  const stored = await readStored();
  if (!stored) return;
  await writeStored({
    ...stored,
    unlockedUntil: new Date(0).toISOString(),
  });
}

/** Wipe verifier + session (factory reset of device unlock). */
export async function wipeLocalUnlock(): Promise<void> {
  await setMeta(META_KEY, '');
}
